import { useJsApiLoader } from '@react-google-maps/api'
import _ from 'lodash'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'
import Supercluster, { PointFeature } from 'supercluster'

import { env } from '@/env'
import { sc, scOptions } from '@/lib/supercluster'
import { CacheAdapter } from '@/shared/infra'

const libraries: ('places' | 'drawing' | 'geometry' | 'visualization')[] = [
  'geometry',
  'places',
]

export interface Circle {
  center: google.maps.LatLngLiteral
  radius: number
}

interface MapContextProps {
  isMapLoaded: boolean
  map: google.maps.Map | null
  setMap: (map: google.maps.Map | null) => void
  clusters: any[]
  setClusters: (clusters: any[]) => void
  updateClusters: () => void
  loadSuperCluster: (points: PointFeature<any>[]) => void
  center: google.maps.LatLngLiteral
  setCenter: (coords: google.maps.LatLngLiteral) => void
  circle: Circle | null
  setCircle: (
    circle: Circle | ((prevCircle: Circle | null) => Circle | null) | null,
  ) => void
  markerMode: boolean
  setMarkerMode: (mode: boolean) => void
  superCluster: typeof sc | null
  enableRadius: boolean
  setEnableRadius: (enable: boolean) => void
  showMap: boolean
  setShowMap: (value: boolean) => void
  showBackground: boolean
  setShowBackground: (value: boolean) => void
  reloadMap?: () => void
  handleRadiusChange: (
    radius: number,
    center?: google.maps.LatLngLiteral,
  ) => void
  saveMapState: () => void
  restoreMapState: () => void
  coordsLoaded: boolean
  setCoordsLoaded: (value: boolean) => void
  isMapLoading: boolean
  setIsMapLoading: (value: boolean) => void
  circleChanged: boolean
  setCircleChanged: (value: boolean) => void
  initialCircle: Circle | null
  setInitialCircle: (circle: Circle | null) => void
  MAX_RADIUS_KM: number
  canShowActions: boolean
  setCanShowActions: (value: boolean) => void
}

const MapContext = createContext<MapContextProps | undefined>(undefined)

export const MapProvider = ({ children }: { children: ReactNode }) => {
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: env.REACT_APP_GOOGLE_MAP_KEY,
    libraries,
  })

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [clusters, setClusters] = useState<any[]>([])
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({
    lat: -15.7942,
    lng: -47.8822,
  })
  const [circle, setCircle] = useState<Circle | null>(null)
  const [markerMode, setMarkerMode] = useState<boolean>(false)
  const [enableRadius, setEnableRadius] = useState<boolean>(false)
  const [showMap, setShowMap] = useState<boolean>(false)
  const [showBackground, setShowBackground] = useState<boolean>(true)
  const [coordsLoaded, setCoordsLoaded] = useState<boolean>(false)
  const [isMapLoading, setIsMapLoading] = useState<boolean>(true)
  const [circleChanged, setCircleChanged] = useState<boolean>(false)
  const [initialCircle, setInitialCircle] = useState<Circle | null>(null)
  const [canShowActions, setCanShowActions] = useState(false)

  const MAX_RADIUS_KM = 10

  const loadSuperCluster = (points: PointFeature<any>[]): void => {
    if (!sc) {
      console.warn(
        'Supercluster instance was undefined, initializing a new instance.',
      )
      const forceScInitialize = new Supercluster(scOptions)

      forceScInitialize.load(points)
      updateClusters()
    } else {
      sc.load(points)
      updateClusters()
    }
  }

  const getClustersForViewport = (
    bounds: google.maps.LatLngBounds | undefined,
    zoom: number | undefined,
  ): any[] => {
    if (!bounds || !zoom) return []

    const clusters = sc.getClusters(
      [
        bounds.getSouthWest().lng(),
        bounds.getSouthWest().lat(),
        bounds.getNorthEast().lng(),
        bounds.getNorthEast().lat(),
      ],
      zoom,
    )
    return clusters
  }

  const debouncedUpdateClusters = _.debounce(() => {
    if (sc && map && markerMode) {
      const bounds = map.getBounds()
      if (bounds) {
        const zoom = map.getZoom() as number

        const newClusters = getClustersForViewport(bounds, zoom)
        if (!_.isEqual(clusters, newClusters)) {
          setClusters(newClusters)
        }
      } else {
        console.error('Bounds is undefined')
      }
    } else {
      console.error('Supercluster, map, markerMode, or clusters are undefined')
    }
  }, 200)

  const updateClusters = useCallback(() => {
    debouncedUpdateClusters()
  }, [debouncedUpdateClusters])

  const handleRadiusChange = (
    radius: number,
    center?: google.maps.LatLngLiteral,
  ) => {
    if (center) {
      setCircle((prevCircle) => {
        const newCircle = { center, radius }
        if (
          prevCircle &&
          (prevCircle.radius !== radius ||
            prevCircle?.center?.lat !== center.lat ||
            prevCircle?.center.lng !== center.lng)
        ) {
          setCircleChanged(true)
        }
        return newCircle
      })
    } else if (circle) {
      setCircle((prevCircle) => {
        if (prevCircle) {
          const newCircle = { ...prevCircle, radius }
          if (prevCircle.radius !== radius) {
            setCircleChanged(true)
          }
          return newCircle
        }
        return prevCircle
      })
    }
  }

  const reloadMap = () => {
    if (map) {
      const currentCenter = map.getCenter()
      if (currentCenter) {
        map.panTo(currentCenter)
      }
    }
  }

  const saveMapState = () => {
    if (map) {
      const currentCenter = map.getCenter()
      const zoom = map.getZoom()
      const mapState = {
        center: currentCenter
          ? { lat: currentCenter.lat(), lng: currentCenter.lng() }
          : null,
        zoom,
        showMap,
        coordsLoaded,
        isMapLoading,
      }

      CacheAdapter.set('mapState', mapState)
    }
  }

  const restoreMapState = () => {
    const savedState = CacheAdapter.get('mapState')

    if (Object.keys(savedState).length > 0) {
      const { center, zoom, showMap, coordsLoaded, isMapLoading } = savedState

      if (center && center.lat !== 0 && center.lng !== 0) {
        setCenter(center)

        if (zoom && map) map.setZoom(zoom)
        setShowMap(showMap)
        setCoordsLoaded(coordsLoaded)
        setIsMapLoading(isMapLoading)
        map?.panTo(center)
      } else {
        console.error('Invalid center coordinates', center)
        CacheAdapter.set('mapState')
      }
    }
  }

  return (
    <MapContext.Provider
      value={{
        isMapLoaded,
        map,
        setMap,
        clusters,
        setClusters,
        updateClusters,
        loadSuperCluster,
        superCluster: sc,
        center,
        setCenter,
        circle,
        setCircle,
        markerMode,
        setMarkerMode,
        enableRadius,
        setEnableRadius,
        showMap,
        setShowMap,
        showBackground,
        setShowBackground,
        reloadMap,
        handleRadiusChange,
        saveMapState,
        restoreMapState,
        coordsLoaded,
        setCoordsLoaded,
        isMapLoading,
        setIsMapLoading,
        circleChanged,
        setCircleChanged,
        initialCircle,
        setInitialCircle,
        MAX_RADIUS_KM,
        canShowActions,
        setCanShowActions,
      }}
    >
      {children}
    </MapContext.Provider>
  )
}

export const useMapContext = (): MapContextProps => {
  const context = useContext(MapContext)
  if (!context) {
    throw new Error('useMapContext must be used within a MapProvider')
  }
  return context
}
